import { observable, action } from 'mobx';
import api from 'core/util/api';
import { Socket } from 'core/util';
import DiscoveryCollection from './DiscoveryCollection';

export class DiscoveryStore {
  @observable
  hasInitialized = false;

  socket;

  collection = new DiscoveryCollection();

  @action
  initialize() {
    const { $auth } = this.store;
    if (!$auth.isSubtenantUser && $auth.hasPermission('recon.enabled', { overrideForSudo: false })) {
      this.collection.fetch().then(
        action(() => {
          this.hasInitialized = true;
        })
      );

      this.socket = new Socket({
        outType: 'nmsDiscovery',
        inType: 'nmsDiscovery',
        frequency: 30,
        delaySend: true,
        listenToBroadcast: true,
        onSuccess: this.handleDiscoveryResponse,
        onError(err) {
          console.warn('Received Discovery Socket Error', err);
        },
        onReconnect: () => {
          this.socket.send();
        }
      });

      this.socket.send({ action: 'INIT' });
    }

    return Promise.resolve();
  }

  start() {
    this.socket.send({ action: 'HYDRATE' });
    return null;
  }

  // kicks off the discovery through Ranger
  startDiscovery(params) {
    const { address_ranges, credentials, agent: agentId } = params;

    const body = {
      discoveryID: `d${Date.now()}`,
      credentials,
      agentId,
      address_ranges
    };

    return api.post('/api/ui/discovery/start', { body }).then((result) => {
      this.collection.add(result);
      return result;
    });
  }

  @action
  cancelDiscovery(model, onDismiss) {
    const body = { discoveryID: model.attributes.id, agentId: model.attributes.agent_id };
    return api.post('/api/ui/discovery/cancel', { body }).then((result) => {
      const canceledAt = new Date().toISOString();
      this.collection.remove(model.attributes.id);
      model.set('canceled', canceledAt);
      if (onDismiss) {
        onDismiss();
      }
      return result;
    });
  }

  rangerInspect(params) {
    const device = this.store.$devices.deviceSummaries.find((d) => d.device_name === params.deviceName);

    if (!device) {
      console.warn('Device not found');
      return Promise.reject();
    }

    const body = {
      agentId: device.device_kentik_agent_id,
      deviceName: params.deviceName,
      deviceId: device.id,
      fetchMetrics: params.fetchMetrics
    };

    return api.post('/api/ui/discovery/ranger-inspect', { body });
  }

  rangerTestConnection(params) {
    const payload = {
      agent_id: params.agentId,
      credential_name: params.credentialName,
      ranger_snmp_ip: params.rangerSnmpIp,
      port: params.port || '161',
      timeout: '2s'
    };

    return api.post('/api/ui/discovery/ranger-test-connection', { body: payload });
  }

  rangerGnmiTestConnection(params) {
    const body = {
      agent_id: params.agentId,
      credential_name: params.credentialName,
      gnmi_ip: params.gnmiIp,
      port: params.port ?? 6030,
      timeout: `${params.timeout ?? 2}s`,
      use_ssl: params.useSsl
    };
    return api.post('/api/ui/discovery/ranger-gnmi-test-connection', { body });
  }

  startSnmpWalk(params) {
    const device = this.store.$devices.getDeviceSummaryByName({ deviceName: params.deviceName });

    if (!device) {
      return Promise.reject(new Error('Device not found'));
    }

    const body = {
      agentId: device.device_kentik_agent_id,
      deviceName: params.deviceName,
      deviceId: device.id,
      oid: params.oid
    };

    return api.post('/api/ui/discovery/walk', { body });
  }

  getSnmpWalkResult(params) {
    const device = this.store.$devices.getDeviceSummaryByName({ deviceName: params.deviceName });

    if (!device) {
      return Promise.reject(new Error('Device not found'));
    }

    const body = {
      agentId: device.device_kentik_agent_id,
      deviceId: device.id,
      walkId: params.walkId
    };

    return api.post('/api/ui/discovery/walk/result', { body });
  }

  cancelSnmpWalk(params) {
    const device = this.store.$devices.getDeviceSummaryByName({ deviceName: params.deviceName });

    if (!device) {
      return Promise.reject(new Error('Device not found'));
    }

    const body = {
      agentId: device.device_kentik_agent_id,
      deviceName: params.deviceName,
      deviceId: device.id
    };

    return api.post('/api/ui/discovery/walk/cancel', { body });
  }

  startGnmiSubscribeOnce(params) {
    const device = this.store.$devices.deviceSummaries.find((d) => d.device_name === params.deviceName);

    if (!device) {
      console.warn(`Device not found: ${params.deviceName}`);
      return Promise.reject(new Error(`Device not found: ${params.deviceName}`));
    }

    const body = {
      agentId: device.device_kentik_agent_id,
      deviceName: params.deviceName,
      path: params.path,
      encoding: params.encoding
    };

    return api.post('/api/ui/discovery/gnmi/subscribe-once', { body });
  }

  startGnmiCapabilities(params) {
    const device = this.store.$devices.deviceSummaries.find((d) => d.device_name === params.deviceName);

    if (!device) {
      console.warn(`Device not found: ${params.deviceName}`);
      return Promise.reject(new Error(`Device not found: ${params.deviceName}`));
    }

    const body = {
      agentId: device.device_kentik_agent_id,
      deviceName: params.deviceName
    };

    return api.post('/api/ui/discovery/gnmi/capabilities', { body });
  }

  refreshInterfaceMetadata(params) {
    const device = this.store.$devices.deviceSummaries.find((d) => d.device_name === params.deviceName);

    if (!device) {
      console.warn(`Device not found: ${params.deviceName}`);
      return Promise.reject(new Error(`Device not found: ${params.deviceName}`));
    }

    const body = {
      agentId: device.device_kentik_agent_id,
      deviceName: params.deviceName,
      deviceId: device.id
    };

    return api.post('/api/ui/discovery/refresh-metadata', { body });
  }

  // Send a flag to force update init stats
  hydrate() {
    this.socket.send({ action: 'HYDRATE' });
  }

  @action
  handleDiscoveryResponse = (res) => {
    const { discoveryId, type } = res;
    const data = res[type];

    if (type === 'update' && discoveryId) {
      const discoveryModel = this.collection.get(discoveryId);

      if (discoveryModel) {
        discoveryModel.update(data);
        if (data.complete) {
          discoveryModel.set('completed', new Date());
        }
      } else {
        // New discovery inbound
        this.collection.add(data);
      }
    }

    return null;
  };
}

export default new DiscoveryStore();
